<template>
    <section class="interactive-attachment-modal esesja-modal"
             :class="{ 'interactive-attachment-modal--image': fileType.isImage, 'interactive-attachment-modal--pdf': fileType.isPfd }"
             ref="modal"
             tabindex="0">
        <section class="esesja-modal__wraper">
            <section class="esesja-modal__wraper__body attachment-viewer" ref="scrollContainer">
                <section class="attachment-viewer--body">
                    <section class="attachment-viewer--body--content">
                        <VuePDF :pdf="fileBlob" :page="internalPage" :scale="pdfScale" v-if="fileType.isPfd" @loaded="pdfLoaded" />
                        <img :src="imageData" v-else-if="fileType.isImage" />
                        <textarea v-else-if="fileType.isText" v-model="textData" style="height:80vh; width: 30vw;"></textarea>
                    </section>
                </section>
            </section>
            <section class="attachment-viewer--footer" ref="footer">
                <section class="left"></section>
                <section class="center">
                    <span v-if="fileType.isPfd">
                        {{$t('com.file.interactive-attachment-modal.page')}} {{internalPage}} {{$t('com.file.attachment-viewer.of')}} {{maxPages}}
                    </span>
                </section>
                <section class="right"></section>
            </section>
        </section>
    </section>
</template>
<script charset="utf-8">
    import { VuePDF, usePDF } from '@tato30/vue-pdf'
    import { createPdfSource } from '@/helpers/pdf.viewer.helper';

    export default {
        name: "InteractiveAttachmentModal",
        components: {
            VuePDF
        },
        props: {
            title: {
                type: String,
                default: ''
            },
            fileData: {
                type: Object,
                require: true
            },
            page: {
                type: Number,
                require: true,
                default: 1
            },
            scale: {
                type: Number,
                require: true,
                default: 100
            },
            scrool: {
                type: Number,
                require: false,
                default: 0
            }

        },
        watch: {
            /*
             * Strona i skala przychodzą jednym komunikatem SignalR (`ChangeAttachemtnPage`),
             * bo admin przy przewijaniu strony dokłada do niego swoje bieżące powiększenie.
             * Dlatego obie prope obsługujemy razem - z osobnych watcherów nie da się wiarygodnie
             * poznać, czy skala przyjechała sama (zoom), czy w pakiecie ze zmianą strony, a od
             * tego zależy, czy wolno wyłączyć dopasowanie do okna. Poleganie na kolejności
             * watcherów byłoby zgadywaniem - `prev` z jednego watchera daje pewną odpowiedź.
             *
             * Skalę po otwarciu załącznika ustawia `prepareFile` prosto z propa, więc ten
             * watcher nie musi obsługiwać pierwszego renderu.
             */
            viewerCommand: {
                handler: function (command, previous) {
                    const pageChanged = !previous || command.page !== previous.page;
                    const scaleChanged = !previous || command.scale !== previous.scale;

                    if (pageChanged) {
                        this.internalPage = command.page;
                    }

                    /*
                     * Skalę honorujemy tylko wtedy, gdy przyjechała sama - wtedy admin
                     * naprawdę kręcił zoomem (`changeScale`) i oddajemy mu sterowanie.
                     * Skala doklejona do przewinięcia strony (`prevPage`/`nextPage`) opisuje
                     * okno admina, nie nakładki, więc jej zastosowanie tylko rozjeżdżałoby
                     * dokument. Dzięki temu zachodzi prosta reguła: samo przewijanie stron
                     * nigdy nie zmienia tego, jak duży jest dokument na wizji.
                     *
                     * Świadomie porównujemy z poprzednią wartością propa, a nie z faktycznie
                     * zastosowaną `internalScale`. Kuszące jest to drugie - skala zignorowana
                     * przy zmianie strony zostaje przecież w propie - ale wtedy admin, który
                     * ustawia powiększenie równe temu, co nakładka już ma, byłby ignorowany
                     * razem z wyłączeniem dopasowania, którego oczekuje. A przypadek odwrotny
                     * i tak jest nieosiągalny: przy niezmienionych propach computed się nie
                     * przelicza i ten watcher w ogóle nie wstaje.
                     */
                    if (!pageChanged && scaleChanged && Number.isFinite(command.scale) && command.scale > 0) {
                        this.internalScale = command.scale;

                        if (this.pdfPageSize.width > 0) {
                            this.fitOnOpen = false;
                        }
                    }

                    if (pageChanged || scaleChanged) {
                        this.$nextTick(this.applyScroll);
                    }
                }
            },
            scrool: {
                deep: true,
                handler: function () {
                    this.$nextTick(this.applyScroll);
                }
            },
            fileData() {
                this.prepareFile();
            }
        },
        computed: {
            /*
             * Para (strona, skala) jako jeden obiekt - przelicza się wyłącznie przy zmianie
             * ktorejś z propów, więc watcher wyżej dostaje komplet starych i nowych wartości.
             */
            viewerCommand() {
                return { page: this.page, scale: this.scale };
            },
            pdfScale() {
                const requestedScale = this.internalScale / 100;
                if (!this.fitOnOpen || this.pdfPageSize.width <= 0 || this.viewportSize.width <= 0 || this.viewportSize.height <= 0) {
                    return requestedScale;
                }

                return Math.min(requestedScale,
                    this.viewportSize.width / this.pdfPageSize.width,
                    this.viewportSize.height / this.pdfPageSize.height);
            }
        },
        data() {
            return {
                localizedTitle: this.$t('com.file.interactive-attachment-modal.attachment-viewer'),
                internalPage: this.page,
                maxPages: 1,
                internalScale: this.scale > 0 ? this.scale : 100,
                fitOnOpen: true,
                pdfPageSize: { width: 0, height: 0 },
                viewportSize: { width: 0, height: 0 },
                viewportObserver: null,
                fileBlob: undefined,
                imageData: undefined,
                textData: undefined,
                fileType: {
                    isPfd: false,
                    isImage: false,
                    isText: false,
                }
            }
        },
        async mounted() {
            this.viewportObserver = new ResizeObserver(this.updateViewportSize);
            // Korzeń modalu, nie kontener strony - patrz komentarz w updateViewportSize.
            this.viewportObserver.observe(this.$refs.modal);
            this.updateViewportSize();
            await this.prepareFile();
        },
        beforeUnmount() {
            this.viewportObserver?.disconnect();
            if (this.imageData) {
                URL.revokeObjectURL(this.imageData);
            }
        },
        methods: {
            updateViewportSize() {
                /*
                 * Mierzymy z korzenia modalu (100vw x 100vh), a NIE z kontenera strony.
                 * Kontener ma teraz szerokość dopasowaną do zawartości, więc pytanie go
                 * o wymiary zamykało pętlę: dopasowanie liczone z jego szerokości zmieniało
                 * skalę, skala zmieniała szerokość, i strona zjeżdżała sama po sobie w dół.
                 * Korzeń modalu nie zależy od zawartości, więc pętli nie ma.
                 *
                 * Od pola ekranu odejmujemy to, co modal dokłada wokół strony: 40px luzu
                 * od krawędzi, 4px ramki wrappera i wysokość stopki z licznikiem stron.
                 */
                const modal = this.$refs.modal;
                if (!modal) {
                    return;
                }

                const CHROME = 44;      // 40px luzu od krawedzi ekranu + 4px ramki wrappera
                const footerHeight = this.$refs.footer ? this.$refs.footer.offsetHeight : 0;

                // Rezerwa na zaokrąglenia canvas i ewentualny pasek przewijania.
                this.viewportSize = {
                    width: Math.max(0, modal.clientWidth - CHROME - 2),
                    height: Math.max(0, modal.clientHeight - CHROME - footerHeight - 2)
                };
            },
            pdfLoaded(viewport) {
                this.pdfPageSize = {
                    width: viewport.width / viewport.scale,
                    height: viewport.height / viewport.scale
                };
                this.updateViewportSize();
                this.$nextTick(this.applyScroll);
            },
            applyScroll() {
                const container = this.$refs.scrollContainer;
                if (!container || !Number.isFinite(this.scrool)) {
                    return;
                }

                const percent = Math.min(100, Math.max(0, this.scrool));
                container.scrollTop = (container.scrollHeight - container.clientHeight) * percent / 100;
            },
            async prepareFile() {
                try {
                    this.pdfPageSize = { width: 0, height: 0 };
                    this.internalPage = this.page;
                    /*
                     * Skala podana przy otwarciu to jawne powiększenie admina - "pokaż
                     * ponownie" (`openAttachmentWithPage`) niesie jego bieżący zoom, więc
                     * odtwarzamy go co do procenta i ukrycie/pokazanie niczego nie zmienia.
                     * Gdy skali nie ma (pierwsze otwarcie z listy punktów - hub ma tam
                     * `int? scale = null`), wracamy do 100% dopasowanego do kadru.
                     */
                    this.internalScale = this.scale > 0 ? this.scale : 100;
                    this.fitOnOpen = !(this.scale > 0);
                    if (this.imageData) {
                        URL.revokeObjectURL(this.imageData);
                        this.imageData = undefined;
                    }
                    if (this.fileData.headers['content-type'] === 'application/pdf') {
                        this.fileType.isPfd = true;
                        this.fileType.isImage = false;
                        this.fileType.isText = false;
                    }
                    else if (this.fileData.headers['content-type'].startsWith("image/")) {
                        this.fileType.isPfd = false;
                        this.fileType.isImage = true;
                        this.fileType.isText = false;
                    }
                    else if (this.fileData.headers['content-type'].startsWith("text/plain")) {
                        this.fileType.isPfd = false;
                        this.fileType.isImage = false;
                        this.fileType.isText = true;
                    }

                    if (this.fileType.isPfd) {
                        const { pdf, pages } = usePDF(createPdfSource(this.fileData.data))
                        this.fileBlob = pdf;
                        this.maxPages = pages;
                        this.internalPage = this.page;
                    }
                    else if (this.fileType.isImage) {
                        let blob = new Blob([this.fileData.data], { type: this.fileData.headers['content-type'] });
                        let urlCreator = window.URL || window.webkitURL;
                        this.imageData = urlCreator.createObjectURL(blob);
                    }
                    else if (this.fileType.isText) {
                        let blob = new Blob([this.fileData.data], { type: this.fileData.headers['content-type'] });
                        this.textData = await blob.text()
                    }
                }
                catch (e) {
                    console.log(e);
                }
                finally {
                    this.$store.commit('hideLoader');
                }
            },
        }
    }
</script>
<style lang="scss" scoped>
    @use "@/assets/scss/esesja-modal.scss";


    /*
     * Szerokość bierzemy z zawartości, nie ze stałych 1200px: strona A4 ma przy 100%
     * raptem 595px, więc sztywne pudełko zostawiało po ~300px białego tła z każdej
     * strony. PDF i grafika dopasowują również wysokość w modyfikatorach poniżej;
     * pozostałe typy załączników zachowują stałą wysokość ramki.
     */
    .interactive-attachment-modal .esesja-modal__wraper {
        width: max-content;
        max-width: calc(100vw - 40px);
        height: calc(100vh - 40px);
        display: flex;
        flex-direction: column;
    }

    .interactive-attachment-modal .esesja-modal__wraper__body {
        min-height: 0;
        max-height: none;
        flex: 1;
        overflow: auto;
        padding: 0px;
    }

    /* Ramka PDF przylega do strony także przed pierwszą zmianą powiększenia.
       Po przekroczeniu wysokości okna dokument przewija się nad widoczną stopką. */
    .interactive-attachment-modal--pdf {
        .esesja-modal__wraper {
            height: auto;
            max-height: calc(100vh - 40px);
        }

        .esesja-modal__wraper__body {
            flex: 0 1 auto;
        }

        .attachment-viewer--body {
            flex-shrink: 0;
        }
    }

    /*
     * Grafika dostaje pudełko wielkości samego obrazka. Stałe 1200 x (100vh - 40px)
     * przy jpg/png zostawiało szeroki biały margines wokół zdjęcia -
     * i w oknie wizualizacji (PublicSession), i w nakładce. Stopka niesie wyłącznie
     * licznik stron PDF-a, więc przy obrazku to kolejny pusty biały pasek.
     *
     * Arytmetyka pudełka - ramka ma przylegać do zdjęcia z równą obwódką ze wszystkich
     * czterech stron, więc limity obrazka muszą uwzględnić to, co modal dokłada wokół:
     *   40px luzu od krawędzi ekranu (stała z reguły bazowej wyżej)
     * +  4px ramki wrappera (2px na stronę, z esesja-modal.scss)
     * + 20px obwódki (10px na stronę, niżej)
     * = 64px, stąd max-width/max-height obrazka.
     * Gdyby obrazek dostał większy limit niż wrapper, `overflow: hidden` uciąłby mu dół
     * zamiast go przeskalować.
     */
    .interactive-attachment-modal--image {
        .esesja-modal__wraper {
            width: auto;
            height: auto;
            max-height: calc(100vh - 40px);
            padding: 10px;
        }

        .esesja-modal__wraper__body {
            flex: 0 1 auto;
            overflow: hidden;
        }

        .attachment-viewer--body {
            min-width: 0;
        }

        .attachment-viewer--body--content > img {
            max-width: calc(100vw - 64px);
            max-height: calc(100vh - 64px);
        }

        .attachment-viewer--footer {
            display: none;
        }
    }

    .attachment-viewer {
        display: flex;
        flex-direction: column;

        &--header {
            display: flex;
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
        }

        /*
         * `margin: auto`, nie `justify-content: center`: gdy strona jest niższa od okna,
         * wolne miejsce rozkłada się po równo na górę i dół zamiast zbierać się w całości
         * pod dokumentem (to był ten "za duży margines na dole"). A gdy strona jest wyższa
         * - przy powiększeniu z admina - marginesy auto schodzą do zera i dokument nadal da
         * się przewinąć do samej góry, czego wyśrodkowanie flexem by nie dało.
         */
        &--body {
            width: max-content;
            min-width: 100%;
            margin: auto;

            &--content {
                width: max-content;
                margin: 0 auto;

                /*
                 * VuePDF renderuje stronę na <canvas>, a ten jest elementem liniowym -
                 * baseline zostawia pod nim 4px na wydłużenia dolne, więc mimo idealnie
                 * wyśrodkowanej kolumny dokument siedział o tyle wyżej niż środek.
                 * `display: block` rozbraja to tak samo jak przy <img> niżej.
                 * `:deep`, bo canvas należy do komponentu VuePDF i nie dostaje atrybutu
                 * scope tego pliku.
                 */
                :deep(canvas) {
                    display: block;
                }

                /* Limity obrazka ustawia modyfikator --image wyżej - obrazek nigdy nie
                   trafia tu w trybie PDF-a, więc własne max-height byłoby martwe. */
                > img {
                    display: block;
                    max-width: min(1194px, calc(100vw - 46px));
                    object-fit: contain;
                }
            }
        }

        &--footer {
            flex-shrink: 0;
            min-height: 30px;
            padding: 4px 12px;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
        }

        &--button {
            height: 32px;
            margin-left: 8px;
            background-color: white;
            border: 1px solid #bbc2ce;
            box-sizing: border-box;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease-in-out;

            &--small {
                width: 30px;
            }

            img {
                width: 2.2em
            }

            &:hover {
                background-color: #e8ebef;
            }
        }
    }
</style>
